import type { ValidationErrors } from '../../../../../utils/validation';
import type { IAfwijkingen } from '../../TCCr/v1/CardioAfwijkingen';
import { translateKey } from '../../../../../translate';
import {
    Dropdown,
    FormDivider,
    InputRadio,
    InputRadioGroup,
    NullableInputText,
    ThirdHeader,
} from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { AddEditAfwijkingen, getEmptyAfwijkingen } from '../../TCCr/v1/CardioAfwijkingen';

export interface ICustomControlData extends IAfwijkingen {
    bloeddruk_response: string | null;
    conclusie_inspanning_ecg: string | null;
    conclusie_rust_ecg: string | null;
    essentiele_veranderingen_in_het_ritme_geleiding_of_ST: string | null;
    hartfrequentierespons_op_inspanning_is: string | null;
    toelichting: string | null;
    veranderingen_in_het_ECG_ritme: string | null;
}

interface ITiecgV1DiagnosticReportAddEditCustomControlFieldsProps {
    customControlData: ICustomControlData;
    errors: ValidationErrors;
    handleChangeCustomControl: (values: Partial<ICustomControlData>) => Promise<void>;
    validateOneCustomControl: (name: string) => Promise<void>;
}

export function TiecgV1DiagnosticReportAddEditCustomControlFields(
    props: ITiecgV1DiagnosticReportAddEditCustomControlFieldsProps
) {
    const handleChangeCustomControl = async (value: Partial<ICustomControlData>) => {
        await props.handleChangeCustomControl(value);
    };

    const validateOneCustomControl = async (name: keyof ICustomControlData) => {
        await props.validateOneCustomControl(name);
    };

    return (
        <>
            <ThirdHeader text={'Bevindingen rust ECG'} />

            <HorizontalFormGroup hasErrors={props.errors.has('customControlData.conclusie_rust_ecg')}>
                <FormLabel isRequired>Conclusie</FormLabel>
                <InputContainer>
                    <InputRadioGroup
                        error={props.errors.get('customControlData.conclusie_rust_ecg')}
                        name="conclusie_rust_ecg"
                        onChange={async (value) =>
                            await handleChangeCustomControl({
                                conclusie_rust_ecg: value,
                                afwijking_in:
                                    value !== 'Afwijkend'
                                        ? getEmptyAfwijkingen().afwijking_in
                                        : props.customControlData.afwijking_in,
                                afwijking_details:
                                    value !== 'Afwijkend'
                                        ? getEmptyAfwijkingen().afwijking_details
                                        : props.customControlData.afwijking_details,
                            })
                        }
                        onDoValidate={async () => await validateOneCustomControl('conclusie_rust_ecg')}
                        options={['Normaal', 'Afwijkend', 'Niet te beoordelen']}
                        value={props.customControlData.conclusie_rust_ecg}
                    />
                </InputContainer>
            </HorizontalFormGroup>

            {props.customControlData.conclusie_rust_ecg &&
                props.customControlData.conclusie_rust_ecg === 'Afwijkend' && (
                    <AddEditAfwijkingen
                        afwijkingen={props.customControlData}
                        errors={props.errors}
                        onChange={async (value) => await handleChangeCustomControl(value)}
                        onDoValidate={async (name, category, disorder) => {
                            const afwijkingenName = name + (disorder ? `.${category}.${disorder}` : `.${category}`);
                            await props.validateOneCustomControl(afwijkingenName);
                        }}
                    />
                )}

            <FormDivider />
            <ThirdHeader text={'Bevindingen inspannings ECG'} />

            <HorizontalFormGroup hasErrors={props.errors.has('customControlData.conclusie_inspanning_ecg')}>
                <FormLabel isRequired>Conclusie</FormLabel>
                <InputContainer>
                    <InputRadioGroup
                        automation="conclusieInspanningEcg"
                        error={props.errors.get('customControlData.conclusie_inspanning_ecg')}
                        name="conclusie_inspanning_ecg"
                        onChange={async (value) => await handleChangeCustomControl({ conclusie_inspanning_ecg: value })}
                        onDoValidate={async () => await validateOneCustomControl('conclusie_inspanning_ecg')}
                        options={['Normaal', 'Afwijkend', 'Niet te beoordelen']}
                        value={props.customControlData.conclusie_inspanning_ecg}
                    />
                </InputContainer>
            </HorizontalFormGroup>

            <HorizontalFormGroup
                hasErrors={props.errors.has('customControlData.essentiele_veranderingen_in_het_ritme_geleiding_of_ST')}
            >
                <FormLabel>Essentiële veranderingen in het ritme, geleiding of ST</FormLabel>
                <InputContainer>
                    <InputRadioGroup
                        error={props.errors.get(
                            'customControlData.essentiele_veranderingen_in_het_ritme_geleiding_of_ST'
                        )}
                        name="essentiele_veranderingen_in_het_ritme_geleiding_of_ST"
                        onChange={async (value) =>
                            await handleChangeCustomControl({
                                essentiele_veranderingen_in_het_ritme_geleiding_of_ST: value,
                            })
                        }
                        onDoValidate={async () =>
                            await validateOneCustomControl('essentiele_veranderingen_in_het_ritme_geleiding_of_ST')
                        }
                        options={['Ja', 'Nee']}
                        value={props.customControlData.essentiele_veranderingen_in_het_ritme_geleiding_of_ST}
                    />
                </InputContainer>
            </HorizontalFormGroup>

            <HorizontalFormGroup hasErrors={props.errors.has('customControlData.veranderingen_in_het_ECG_ritme')}>
                <FormLabel>Veranderingen in het ECG ritme</FormLabel>
                <InputContainer>
                    <InputRadioGroup
                        error={props.errors.get('customControlData.veranderingen_in_het_ECG_ritme')}
                        name="veranderingen_in_het_ECG_ritme"
                        onChange={async (value) => {
                            await handleChangeCustomControl({ veranderingen_in_het_ECG_ritme: value });
                            if (value === 'Nee') {
                                await handleChangeCustomControl({ toelichting: null });
                            }
                        }}
                        onDoValidate={async () => await validateOneCustomControl('veranderingen_in_het_ECG_ritme')}
                        options={['Ja', 'Nee']}
                        value={props.customControlData.veranderingen_in_het_ECG_ritme}
                    />
                </InputContainer>
            </HorizontalFormGroup>

            {props.customControlData.veranderingen_in_het_ECG_ritme === 'Ja' && (
                <HorizontalFormGroup hasErrors={props.errors.has('customControlData.toelichting')}>
                    <FormLabel>Toelichting</FormLabel>
                    <InputContainer>
                        <InputRadioGroup
                            customOption={
                                <InputRadio
                                    label={
                                        <NullableInputText
                                            onChange={async (value) =>
                                                await handleChangeCustomControl({ toelichting: value })
                                            }
                                            onDoValidate={async () => await validateOneCustomControl('toelichting')}
                                            placeholder="Anders, namelijk..."
                                            value={
                                                props.customControlData.toelichting !==
                                                    'PVC’s toenemend tijdens inspanning' &&
                                                props.customControlData.toelichting !==
                                                    'PVC’s afnemend tijdens inspanning'
                                                    ? props.customControlData.toelichting
                                                    : null
                                            }
                                        />
                                    }
                                    name={'toelichting'}
                                    onChange={async (value) =>
                                        await handleChangeCustomControl({ toelichting: value ? value : '' })
                                    }
                                    onValue={''}
                                    value={
                                        props.customControlData.toelichting === 'PVC’s toenemend tijdens inspanning' ||
                                        props.customControlData.toelichting === 'PVC’s afnemend tijdens inspanning' ||
                                        props.customControlData.toelichting === null
                                            ? null
                                            : ''
                                    }
                                />
                            }
                            error={props.errors.get('customControlData.toelichting')}
                            name="toelichting"
                            onChange={async (value) => await handleChangeCustomControl({ toelichting: value })}
                            onDoValidate={async () => await validateOneCustomControl('toelichting')}
                            options={['PVC’s toenemend tijdens inspanning', 'PVC’s afnemend tijdens inspanning']}
                            radioButtonsStacked
                            value={
                                props.customControlData.toelichting === 'PVC’s toenemend tijdens inspanning' ||
                                props.customControlData.toelichting === 'PVC’s afnemend tijdens inspanning'
                                    ? props.customControlData.toelichting
                                    : null
                            }
                        />
                    </InputContainer>
                </HorizontalFormGroup>
            )}

            <HorizontalFormGroup
                hasErrors={props.errors.has('customControlData.hartfrequentierespons_op_inspanning_is')}
            >
                <FormLabel>Hartfrequentierespons op inspanning</FormLabel>
                <InputContainer>
                    <Dropdown
                        error={props.errors.get('customControlData.hartfrequentierespons_op_inspanning_is')}
                        onChange={async (value) =>
                            await handleChangeCustomControl({ hartfrequentierespons_op_inspanning_is: value })
                        }
                        onDoValidate={async () =>
                            await validateOneCustomControl('hartfrequentierespons_op_inspanning_is')
                        }
                        placeholder={translateKey('common.selectOne')}
                        selectOptions={[
                            { value: 'Geëigend', text: 'Geëigend' },
                            { value: 'Onvoldoende', text: 'Onvoldoende' },
                            { value: 'Te groot', text: 'Te groot' },
                        ]}
                        value={props.customControlData.hartfrequentierespons_op_inspanning_is}
                    />
                </InputContainer>
            </HorizontalFormGroup>

            <HorizontalFormGroup hasErrors={props.errors.has('customControlData.bloeddruk_response')}>
                <FormLabel>Bloeddruk response</FormLabel>
                <InputContainer>
                    <Dropdown
                        error={props.errors.get('customControlData.bloeddruk_response')}
                        onChange={async (value) => await handleChangeCustomControl({ bloeddruk_response: value })}
                        onDoValidate={async () => await validateOneCustomControl('bloeddruk_response')}
                        placeholder={translateKey('common.selectOne')}
                        selectOptions={[
                            {
                                value: 'Normale bloeddruk in rust - geëigende respons',
                                text: 'Normale bloeddruk in rust - geëigende respons',
                            },
                            {
                                value: 'Verhoogde bloeddruk in rust - geëigende respons',
                                text: 'Verhoogde bloeddruk in rust - geëigende respons',
                            },
                            {
                                value: 'Normale bloeddruk in rust - daling bij inspanning',
                                text: 'Normale bloeddruk in rust - daling bij inspanning',
                            },
                            {
                                value: 'Verhoogde bloeddruk in rust - daling bij inspanning',
                                text: 'Verhoogde bloeddruk in rust - daling bij inspanning',
                            },
                            {
                                value: 'Normale bloeddruk in rust - excessieve stijging bij inspanning',
                                text: 'Normale bloeddruk in rust - excessieve stijging bij inspanning',
                            },
                        ]}
                        value={props.customControlData.bloeddruk_response}
                    />
                </InputContainer>
            </HorizontalFormGroup>
        </>
    );
}

export function tiecgV1DiagnosticReportInitializeCustomControlData() {
    return {
        ...getEmptyAfwijkingen(),
        conclusie_rust_ecg: null,
        conclusie_inspanning_ecg: null,
        essentiele_veranderingen_in_het_ritme_geleiding_of_ST: null,
        veranderingen_in_het_ECG_ritme: null,
        toelichting: null,
        hartfrequentierespons_op_inspanning_is: null,
        bloeddruk_response: null,
    };
}
